'use client'

import { ReactNode, useEffect, useMemo, useState } from 'react';
import { BetsFilter, BetsFilterList } from '@/features/bets/bets-filter-types';
import { useUnsettledCategoryInfo } from '@/hooks/query';
import type { DropdownEvent } from '@nf/ui';
import { Dropdown } from '@nf/ui';
import { Unsettled } from '@/features/bets/unsettled';
import { DropdownIcon, MyBetIcon } from '@/components/icons/svg-icons';
import { useTranslations } from 'next-intl';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { useAccountInfo, useAccountSabaCoinWallet } from '@/hooks/query/account';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { TabSwitch } from '@/components/widgets/tab-switch';
import { betsPanelStyle } from './bets-panel.style';
import { useBetslipStore } from '@/store/betslip';
import { useStreamingStore } from '@/store/streaming';
import { useFloatBetslip } from '@/features/betslip/hook/use-float-betslip';
import { Betslip } from '@/features/betslip';
import clsx from 'clsx';
import { useAsideBetslip } from '@/features/betslip/hook/use-aside-betslip';

const DATA_TYPE_SABA_COIN = 1;

const DisplayWrapper = ({ className, isShow, children }: {className: string, isShow: boolean, children: ReactNode}) => {
	return (
		<div className={className} style={{ display: isShow? 'block': 'none', height: 'inherit' }}>
			{children}
		</div>
	)
}

const BetslipTab = ({ text, count }: {text:string, count: number}) => {
	const [
		currentMultiTickets,
	] = useBetslipStore(state => [
		state.currentMultiTickets,
	]);
	return (
		<div className={betsPanelStyle.betslipTap}>
			{text}
			{currentMultiTickets.length > 0 ? (
				<div className={clsx('bet-slip__badge', betsPanelStyle.betslipBadge)}>
					{count}
				</div>)
				: null
			}
		</div>
	)
}

export const BetsPanel = () => {
	const t = useTranslations('General');
	const [
		isExpnadChatroom,
		setIsExpnadChatroom,
		
	] = useStreamingStore(
		state => [
			state.isExpnadChatroom,
			state.setIsExpnadChatroom,
		]
	);
	const [
		betslipType,
		isQuickBet,
		currentMultiTickets,
	] = useBetslipStore(state => [
		state.betslipType,
		state.isQuickBet,
		state.currentMultiTickets
	]);
	const { activeAsideBetSlip } = useAsideBetslip();
	const { betsUnsettledCategory, isFetching } = useUnsettledCategoryInfo(true);
	const { activeFloatBetSlip } = useFloatBetslip();
	const { user } = useAccountInfo();
	const view = useAppearanceStore(state => state.view);
	const { data: sabaCoinWallet } = useAccountSabaCoinWallet();
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const [dropdownValue, setDropdownValue] = useState<number>();
	const [dropdownData, setDropdownData] = useState<BetsFilter>({
		datatype: 0,
		ticketcount: 0
	});
    
	const [type, setType] = useState(0);
	const SabaCoinFilter:  BetsFilterList = {
		displayName: t('lbl_RPSabaCoins'),
		filterID: DATA_TYPE_SABA_COIN,
	};

	
	useEffect(() => {
		if (betsUnsettledCategory && betsUnsettledCategory.datatype !== 0) {
			if (sabaCoinWallet &&  !betsUnsettledCategory.filterList?.find(x => x.filterID === 1) && siteConfig?.EnableSabaCoinStatement) {
				betsUnsettledCategory.filterList?.push(SabaCoinFilter);
			} 
			setDropdownData(betsUnsettledCategory);
		}

		if (!betsUnsettledCategory && sabaCoinWallet && siteConfig?.EnableSabaCoinStatement) {
			setDropdownData({
				datatype: 1,
				ticketcount: 0,
				filterList: [SabaCoinFilter]
			});
		}
		

	}, [betsUnsettledCategory])

	useEffect(() => {
		setDropdownValue(dropdownData.datatype);
		setType(dropdownData.datatype);
	}, [dropdownData])

	const onDropdownChange = (e: DropdownEvent) => {
		const value = e.option.value;
		setDropdownValue(value);
		setType(value);
	}

	const [selectedIndex, setSelectedIndex] = useState(0);

	const handleTabChange = (index: number) => {
		// 若chatroom 展開, 需要關閉
		if (isExpnadChatroom) {
			setIsExpnadChatroom(false);
			//select 到 iframe實體並操控
			const iframe = document.querySelector('#chatroom-iframe') as HTMLIFrameElement;
			iframe?.contentWindow?.postMessage({ type: 'StreamerChatRoom', action: { expandChat: 0 } }, '*');
		}
		setSelectedIndex(index);
	};

	const tabs = useMemo(() => [
		<BetslipTab key={0} text={t('Betsilp')} count={currentMultiTickets.length} />, 
		view === ViewEnum.mars ? t('Statement') : t('lbl_MyBets')
	], [currentMultiTickets.length, t, view]);

	// 因為css需要根據投注模式改變
	const isMutipleBet =  currentMultiTickets.length > 1;
	const isIndependentMode = user?.BettingMode === 1;
	return (
		<>
			{!activeFloatBetSlip ? (
				<TabSwitch
					className={clsx('tab-switch', betsPanelStyle.tabSwitch)}
					tabs={tabs}
					selectedIndex={selectedIndex}
					onTabChange={handleTabChange}
					variant={{
						size: 'medium',
						theme: 'bet',
					}}
				/>

			): null}
			<div className={betsPanelStyle.container}>
				<DisplayWrapper 
					className={clsx(
						betsPanelStyle.asideBetSlip, 
						{ [betsPanelStyle.hasBet]: currentMultiTickets.length > 0 },
						{ [betsPanelStyle.betslipAsideEmpty]: betslipType === 'empty' && activeAsideBetSlip || isQuickBet && activeAsideBetSlip },
						{ [betsPanelStyle.betslipAsideGalaxyIndep]: isMutipleBet && isIndependentMode && activeAsideBetSlip && view === ViewEnum.galaxy  },
					)} 
					isShow={selectedIndex === 0 && !activeFloatBetSlip}
				>
					<Betslip renderStatus="aside" />
				</DisplayWrapper>
				<DisplayWrapper className="aside-panel" isShow={(!activeFloatBetSlip && selectedIndex === 1) || activeFloatBetSlip}>
					<div className="aside-panel__header">
						<MyBetIcon />
						<span className="aside-panel__title">{view === ViewEnum.mars ? t('Statement') : t('lbl_MyBets')}</span>
						{dropdownData?.filterList?.length ? (
							<Dropdown
								options={dropdownData.filterList?.map(item => ({ label: item.displayName, value: item.filterID })) ?? []}
								value={dropdownValue}
								variant="right"
								onChange={onDropdownChange}
								iconTemplate={() => <DropdownIcon />}
							/>
						) : null}
					</div>
					<Unsettled
						isAside={true}
						asideType={type}
						isFetchingCategory={isFetching}
						isOutSabaCoinSelected={type === DATA_TYPE_SABA_COIN}
					/>
				</DisplayWrapper>
			</div>
		</>
		
		
	)
}